import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { FaInstagram, FaEnvelope } from 'react-icons/fa';
import { useMediaQuery } from 'react-responsive';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import DesktopVideo from './DesktopVideo.jsx';
import MobileVideo from './MobileVideo.jsx';
import { useTranslation } from 'react-i18next';

gsap.registerPlugin(ScrollTrigger);

const Main = () => {
  const isMobile = useMediaQuery({ maxWidth: 1000 });
  const mainRef = useRef(null);
  const { t } = useTranslation();

  const emailAddress = 'webapp.naim@gmail.com';
  const handleEmailClick = () => (window.location.href = `mailto:${emailAddress}`);

  // Timeline: white overlay + text intro + reveal background video
  useLayoutEffect(() => {
    if (!mainRef.current) return;
    const reduceMotion =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduceMotion) {
      gsap.set('.hero-white-overlay', { autoAlpha: 0 });
      gsap.set('.hero-text-animate', { autoAlpha: 1, filter: 'none' });
      gsap.set('.hero-text-color', { color: '#ffffff' });
      return;
    }

    const ctx = gsap.context(() => {
      const targets = gsap.utils.toArray('.hero-text-animate');
      gsap.set(targets, { willChange: 'opacity, filter' });

      const tl = gsap.timeline();
      tl.set('.hero-white-overlay', { autoAlpha: 1 });
      tl.fromTo(
        targets,
        { autoAlpha: 0, filter: 'blur(20px)' },
        {
          autoAlpha: 1,
          filter: 'blur(0px)',
          duration: 1,
          ease: 'power2.out',
          stagger: 0.15,
        },
        0
      );
      tl.to('.hero-white-overlay', { autoAlpha: 0, duration: 1.2, ease: 'power2.inOut' }, 2);
      tl.to(
        '.hero-text-color',
        { color: '#ffffff', duration: 1, ease: 'power2.out' },
        2
      );

      // Capsule Dynamic Breathing
      gsap.to('.social-capsule', {
        y: -3,
        borderColor: 'rgba(255, 255, 255, 0.3)',
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        boxShadow: '0 5px 15px rgba(255, 255, 255, 0.1)',
        duration: 3,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut',
        delay: 2
      });
    }, mainRef);

    return () => ctx.revert();
  }, []);

  // Fade-out sincronizzato allo scroll
  useEffect(() => {
    const element = mainRef.current;
    if (!element) return;

    const tween = gsap.fromTo(
      element,
      { opacity: 1 },
      {
        opacity: 0,
        ease: 'power1.out',
        scrollTrigger: {
          trigger: element,
          start: 'bottom bottom',
          end: 'bottom top',
          scrub: true,
        },
      }
    );

    return () => {
      tween.scrollTrigger?.kill();
      tween.kill();
    };
  }, []);

  return (
    <div id="main" ref={mainRef} className="relative h-screen w-screen overflow-hidden md:-left-1">
      <div className="absolute inset-0">
        {isMobile ? <MobileVideo /> : <DesktopVideo />}
      </div>

      <div className="absolute inset-0 z-10 bg-slate-700/60  mix-blend-multiply pointer-events-none" />
      <div className="hero-white-overlay absolute inset-0 z-20 bg-white pointer-events-none" />

      <div className="relative z-30 flex h-full w-full flex-col items-center justify-center overflow-y-auto px-6">
        <div className="flex w-full max-w-[1040px] flex-col items-start text-left tracking-wide">
          <span className="hero-text-animate hero-text-color uppercase text-xs tracking-[0.4em] text-accent/80">
            {t('hero.eyebrow')}
          </span>
          <h1 className="hero-title hero-text-animate hero-text-color mt-6 text-4xl sm:text-5xl lg:text-6xl xl:text-7xl w-full max-w-[840px] font-serif leading-tight text-primary drop-shadow-md">
            {t('hero.title')} <span className="hero-text-color text-secondary">{t('hero.highlight')}</span>
          </h1>
          <p className="hero-subtitle hero-text-animate hero-text-color mt-8 text-md md:text-xl font-Crimson leading-relaxed font-light max-w-2xl opacity-90 text-secondary tracking-[0.2em]">
            {t('hero.subtitle')}
          </p>
        </div>

        <div className="hero-text-animate social-capsule mt-20 flex items-center gap-8 px-10 py-4 rounded-full border border-white/10 bg-black/5 backdrop-blur-md shadow-sm hover:bg-black/20 hover:border-white/20 hover:shadow-md">
          <a
            href={`mailto:${emailAddress}`}
            onClick={handleEmailClick}
            className="text-white/80 transition-all duration-300 hover:text-white hover:scale-110"
            aria-label="Email"
          >
            <FaEnvelope size={20} />
          </a>

          <div className="h-4 w-[1px] bg-white/20"></div>

          <a
            href="https://www.instagram.com/naif92_naim/?hl=it"
            target="_blank"
            rel="noopener noreferrer"
            className="text-white/80 transition-all duration-300 hover:text-white hover:scale-110"
            aria-label="Instagram"
          >
            <FaInstagram size={22} />
          </a>
        </div>
      </div>
    </div>
  );
};

export default Main;
